import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCircleCheck,
  faPenToSquare,
  faPlus,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import { Button, SearchBar } from "../../components/ui";
import { disableVoucher, listVouchers } from "./voucherApi";
import "./Voucher.css";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-MY", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

function VoucherPage() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [disablingId, setDisablingId] = useState(null);

  const fetchVouchers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listVouchers();
      setVouchers(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data ||
          err.message ||
          "Failed to load vouchers.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  const filteredVouchers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return vouchers.filter((voucher) => {
      const matchesQuery =
        !query ||
        voucher.code.toLowerCase().includes(query) ||
        voucher.discountType.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && voucher.active) ||
        (statusFilter === "inactive" && !voucher.active);

      const matchesType =
        typeFilter === "all" ||
        voucher.discountType.toLowerCase() === typeFilter;

      return matchesQuery && matchesStatus && matchesType;
    });
  }, [searchQuery, statusFilter, typeFilter, vouchers]);

  const activeCount = vouchers.filter((voucher) => voucher.active).length;

  const handleDisable = async (voucherId) => {
    setActionError("");
    setDisablingId(voucherId);
    try {
      await disableVoucher(voucherId);
      setVouchers((current) =>
        current.map((voucher) =>
          String(voucher.id) === String(voucherId)
            ? { ...voucher, active: false }
            : voucher,
        ),
      );
    } catch (err) {
      setActionError(
        err.response?.data?.message ||
          err.response?.data ||
          err.message ||
          "Failed to disable voucher.",
      );
    } finally {
      setDisablingId(null);
    }
  };

  return (
    <div className="voucher-page">
      <div className="voucher-header">
        <div>
          <h1 className="page-title">Vouchers</h1>
          <p className="page-subtitle">
            {loading
              ? "Loading vouchers..."
              : `${vouchers.length} total · ${activeCount} active`}
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => navigate("/voucher/new")}
        >
          Create Voucher
        </Button>
      </div>

      {error && (
        <div className="voucher-banner voucher-banner-error">{error}</div>
      )}
      {actionError && (
        <div className="voucher-banner voucher-banner-error">{actionError}</div>
      )}

      <div className="voucher-toolbar">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by code or discount type..."
          fullWidth
          disabled={loading}
        />
        <div className="voucher-filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={loading}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            disabled={loading}
          >
            <option value="all">All types</option>
            <option value="percentage">Percentage</option>
            <option value="amount">Amount</option>
          </select>
        </div>
      </div>

      <div className="voucher-table-wrap">
        {loading ? (
          <div className="voucher-empty-state">Loading vouchers...</div>
        ) : filteredVouchers.length === 0 ? (
          <div className="voucher-empty-state">
            <FontAwesomeIcon icon={faTag} className="voucher-empty-icon" />
            <h3>No vouchers found</h3>
            <p>
              {vouchers.length === 0
                ? "Create your first voucher to get started."
                : "Try adjusting the search or filters."}
            </p>
          </div>
        ) : (
          <table className="voucher-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount Type</th>
                <th>Discount Value</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Expiry Date</th>
                <th>Expired</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.id}>
                  <td>
                    <div className="voucher-code-cell">
                      <Link
                        className="voucher-code-link"
                        to={`/voucher/${voucher.id}`}
                      >
                        {voucher.code}
                      </Link>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`voucher-pill voucher-pill-${voucher.discountType.toLowerCase()}`}
                    >
                      {voucher.discountType || "—"}
                    </span>
                  </td>
                  <td>{voucher.discountValue}</td>
                  <td>{formatDate(voucher.createdAt)}</td>
                  <td>{formatDate(voucher.updatedAt)}</td>
                  <td>{formatDate(voucher.expiryDate)}</td>
                  <td>{voucher.expired ? "Yes" : "No"}</td>
                  <td>
                    <span
                      className={`voucher-status ${voucher.active ? "active" : "inactive"}`}
                    >
                      <FontAwesomeIcon
                        icon={voucher.active ? faCircleCheck : faBan}
                      />
                      {voucher.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="voucher-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                        onClick={() => navigate(`/voucher/${voucher.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        leftIcon={<FontAwesomeIcon icon={faBan} />}
                        disabled={!voucher.active || disablingId === voucher.id}
                        loading={disablingId === voucher.id}
                        onClick={() => handleDisable(voucher.id)}
                      >
                        Disable
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default VoucherPage;
